/**
 * Které parametry uzlu jsou povinné — ODVOZENO ze zod schématu v registru, ne ručním
 * seznamem. Ruční flag by se dřív nebo později rozešel se schématem a UI by lhalo.
 *
 * Používá to editor (hvězdička u pole + živá validace) i drift testy.
 */

import { NODE_PARAM_SCHEMAS } from "@globals/automations/registry";
import { AUTOMATION_NODE_TYPES, type AutomationNodeType } from "@globals/automations/types";
import type { z } from "zod";

/** Vrátí `shape` objektového schématu, nebo undefined u neobjektových schémat. */
const shapeOf = (type: AutomationNodeType): Record<string, z.ZodTypeAny> | undefined => {
  const schema = NODE_PARAM_SCHEMAS[type] as unknown as { def?: { shape?: Record<string, z.ZodTypeAny> } };
  return schema?.def?.shape;
};

/** Všechny klíče parametrů uzlu podle schématu. */
export const paramKeys = (type: AutomationNodeType): string[] => Object.keys(shapeOf(type) ?? {});

/**
 * Klíče, které MUSÍ být vyplněné. Pole s `.default()` nebo `.optional()` povinné nejsou —
 * zod je doplní / povolí chybějící.
 */
export const requiredParamKeys = (type: AutomationNodeType): Set<string> => {
  const shape = shapeOf(type);
  if (!shape) return new Set();
  const out = new Set<string>();
  for (const [key, field] of Object.entries(shape)) {
    const f = field as unknown as { safeParse: (v: unknown) => { success: boolean } };
    // Povinné = schéma pole odmítne `undefined` (default i optional ho naopak přijmou).
    if (!f.safeParse(undefined).success) out.add(key);
  }
  return out;
};

/** Předpočítaná mapa (registr je konstantní, ať se to nepočítá per render). */
export const REQUIRED_PARAMS: Record<AutomationNodeType, Set<string>> = Object.fromEntries(
  AUTOMATION_NODE_TYPES.map((t) => [t, requiredParamKeys(t)])
) as Record<AutomationNodeType, Set<string>>;

/**
 * Ověří jednu hodnotu proti schématu daného pole (živá validace při psaní).
 * Vrací `undefined` když je v pořádku, jinak krátkou českou hlášku.
 */
export const validateParam = (type: AutomationNodeType, key: string, value: unknown): string | undefined => {
  const field = shapeOf(type)?.[key];
  if (!field) return undefined;
  const r = (field as unknown as { safeParse: (v: unknown) => { success: boolean; error?: z.ZodError } }).safeParse(
    value
  );
  if (r.success) return undefined;
  const issue = r.error?.issues?.[0];
  if (!issue) return "Neplatná hodnota.";
  const empty = value === undefined || value === null || value === "";
  switch (issue.code) {
    case "invalid_type":
      return empty ? "Povinné pole." : "Neplatný typ hodnoty.";
    case "too_small":
      // U prázdného textu/výběru je „Minimum je 1" nesmyslná hláška — je to prostě povinné pole.
      if (empty) return "Povinné pole.";
      return `Minimum je ${String((issue as unknown as { minimum?: unknown }).minimum ?? "?")}.`;
    case "too_big":
      return `Maximum je ${String((issue as unknown as { maximum?: unknown }).maximum ?? "?")}.`;
    case "invalid_format":
      return "Nesprávný formát.";
    default:
      return "Neplatná hodnota.";
  }
};
